import { useState, useEffect } from 'react';
import {
  makeStyles,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuItemRadio,
  MenuDivider,
  MenuGroupHeader,
  Button,
} from '@fluentui/react-components';
import {
  SignOutRegular,
  NavigationRegular,
  WeatherSunnyRegular,
  WeatherMoonRegular,
  DesktopRegular,
  DismissRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../../hooks/useAuth';
import { useAuthUser } from '../../stores/auth';
import { useAppRole } from '../../hooks/useAppRole';
import { htsLogos } from '../../themes/htsTheme';
import { htsFont } from '../../themes/htsTokens';
import { setThemePreference, useThemePreference, type ThemePreference } from '../../themes/themeMode';
import { useLocation, useNavigate } from 'react-router-dom';
import { NAV_ITEMS, isNavItemActive } from './navItems';

const useStyles = makeStyles({
  // 64px navy header, continuous with the rail (app-shell standard).
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    // Own the top safe area (native iPad shell, contentInset 'never'): the
    // padded strip renders in the header's own navy under the iPadOS status
    // pill. --hts-safe-top is injected by the native shell (the webview
    // reports env()=0 despite the status bar; the channel carries HALF the
    // bar height — device-test: full inset + breathing was 'too much, cut
    // it in 1/2'). max() keeps browsers unchanged (both 0 there).
    height: 'calc(64px + max(env(safe-area-inset-top, 0px), var(--hts-safe-top, 0px)) + 4px)',
    // Hairline at the very top of the bar: on the iPad the status zone and
    // header are the same navy — a 1px lighter line makes the boundary
    // legible instead of reading as one continuous slab.
    borderTop: '1px solid rgba(255, 255, 255, 0.14)',
    boxSizing: 'border-box',
    flexShrink: 0,
    paddingLeft: '16px',
    paddingRight: '16px',
    // paddingTop MUST come after any padding shorthand — the shorthand
    // resets padding-top and silently moves the safe-area inset to the
    // bar's bottom (portal app device-test: 'padding went all to the
    // bottom'). Individual longhands here, listed last for safety.
    paddingTop: 'calc(max(env(safe-area-inset-top, 0px), var(--hts-safe-top, 0px)) + 4px)',
    backgroundColor: 'var(--hts-shell-bg)',
    color: '#FFFFFF',
    // No bottom border — header and rail are one continuous surface (MC).
  },
  hamburger: {
    display: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    '@media (max-width: 767px)': {
      display: 'inline-flex',
    },
  },
  // Compact pages (install/survey tools) have no rail — keep nav reachable.
  hamburgerCompact: {
    display: 'inline-flex',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  logo: {
    height: '40px', // matches Mission Control header logo (h-10)
    width: 'auto',
    objectFit: 'contain',
  },
  appTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    '@media (max-width: 480px)': {
      display: 'none',
    },
  },
  rightSection: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  version: {
    fontSize: '11px',
    fontFamily: htsFont.mono,
    letterSpacing: '0.02em',
    color: 'rgba(255, 255, 255, 0.55)',
  },
  userBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '@media (max-width: 640px)': {
      display: 'none',
    },
  },
  userName: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.25,
    color: '#FFFFFF',
  },
  userRole: {
    fontSize: '11px',
    lineHeight: 1.25,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'rgba(255, 255, 255, 0.5)',
  },
  avatarTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.25)',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',
    ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
  },

  // Mobile drawer: full-height left sheet, navy background, above content.
  drawerRoot: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    pointerEvents: 'none',
  },
  drawerRootOpen: {
    pointerEvents: 'auto',
  },
  drawerBackdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '0.2s',
  },
  drawerBackdropVisible: {
    opacity: 1,
  },
  drawerSheet: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '280px',
    maxWidth: '80vw',
    backgroundColor: 'var(--hts-shell-bg)',
    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.35)',
    transform: 'translateX(-100%)',
    transitionProperty: 'transform',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001,
  },
  drawerSheetOpen: {
    transform: 'translateX(0)',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    height: '64px',
    flexShrink: 0,
    paddingLeft: '16px',
    paddingRight: '8px',
    backgroundColor: 'var(--hts-shell-bg)',
  },
  drawerLogo: {
    height: '40px',
    width: 'auto',
    objectFit: 'contain',
  },
  drawerClose: {
    color: '#FFFFFF',
  },
  drawerDivider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    marginLeft: '16px',
    marginRight: '16px',
    flexShrink: 0,
  },
  drawerSearchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    flexShrink: 0,
  },
  drawerSearchIcon: {
    fontSize: '18px',
    width: '18px',
    height: '18px',
    color: 'rgba(255, 255, 255, 0.5)',
    flexShrink: 0,
  },
  drawerSearchInput: {
    flex: 1,
    height: '36px',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: '#FFFFFF',
    fontFamily: 'inherit',
    fontSize: '14px',
    outline: 'none',
    '::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
    },
  },
  drawerBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 12px',
  },
  drawerNav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  drawerItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    height: '44px',
    paddingLeft: '12px',
    paddingRight: '12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.65)',
    transitionProperty: 'color, background-color',
    transitionDuration: '0.15s',
    ':hover': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  drawerItemActive: {
    backgroundColor: 'rgba(0, 184, 235, 0.15)',
    color: 'var(--hts-rail-active-ink)',
    ':hover': {
      color: 'var(--hts-rail-active-ink)',
      backgroundColor: 'rgba(0, 184, 235, 0.15)',
    },
  },
  drawerIndicator: {
    position: 'absolute',
    left: 0,
    top: '6px',
    bottom: '6px',
    width: '3px',
    borderTopRightRadius: '3px',
    borderBottomRightRadius: '3px',
    backgroundColor: 'var(--hts-nav-active-ink)',
  },
  drawerIcon: {
    fontSize: '22px',
    width: '22px',
    height: '22px',
    flexShrink: 0,
  },
});

const THEME_OPTIONS: Array<{ value: ThemePreference; label: string; icon: React.ReactElement }> = [
  { value: 'light', label: 'Light', icon: <WeatherSunnyRegular /> },
  { value: 'dark', label: 'Dark', icon: <WeatherMoonRegular /> },
  { value: 'system', label: 'System', icon: <DesktopRegular /> },
];

interface HeaderProps {
  compact?: boolean;
}

/**
 * The top header bar (HTS app-shell standard): 64px navy, continuous with the
 * rail. White logo + app name left; user block (name + role) and avatar menu
 * right. The avatar menu carries Appearance (Light/Dark/System) and Sign out;
 * Site Survey has no notification system, so no bell. Mobile (<768px) and
 * compact tool pages get a hamburger opening a Mission Control-style drawer
 * with the rail items, a search row, and the white HTS logo.
 */
export const Header: React.FC<HeaderProps> = ({ compact = false }) => {
  const styles = useStyles();
  const { account, signOut } = useAuth();
  // The app-session user is the reliable identity for the header: MSAL's
  // cached account can be absent while the session cookie is perfectly valid
  // (dev-bypass sessions, cleared MSAL cache) — the user block must not
  // vanish for a signed-in user.
  const sessionUser = useAuthUser();
  // DEV-ONLY fallback (same VITE_DEV_AUTH_BYPASS flag as ProtectedRoute's
  // bypass): the rig renders pages with no MSAL account and no store user, so
  // the header would show no identity at all. Inert in production builds.
  const devBypassIdentity = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
    ? { name: 'Dev Admin (local)' }
    : null;
  const displayName = sessionUser?.name || account?.name || devBypassIdentity?.name || 'User';
  const { role } = useAppRole();
  const theme = useThemePreference();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawerOpen]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const initials = (displayName ?? '?')
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className={styles.header}>
      <Button
        appearance="subtle"
        aria-label="Open navigation menu"
        icon={<NavigationRegular />}
        className={`${styles.hamburger} ${compact ? styles.hamburgerCompact : ''}`}
        onClick={() => setDrawerOpen(true)}
      />
      <div className={styles.leftSection}>
        <img src={htsLogos.white} alt="Harris Technology Services" className={styles.logo} />
        <span className={styles.appTitle} data-testid="header-app-name">Site Survey</span>
      </div>
      <div className={styles.rightSection}>
        {compact && (
          <span className={styles.version}>v{__APP_VERSION__}</span>
        )}
        {(sessionUser || account || devBypassIdentity) && (
          <>
            <div className={styles.userBlock}>
              <span className={styles.userName} data-testid="header-user-name">{displayName}</span>
              <span className={styles.userRole}>{role}</span>
            </div>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <button type="button" aria-label="User menu" className={styles.avatarTrigger}>
                  {initials}
                </button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList
                  checkedValues={{ theme: [theme] }}
                  onCheckedValueChange={(_e, data) => {
                    const next = data.checkedItems[0] as ThemePreference | undefined;
                    if (next) setThemePreference(next);
                  }}
                >
                  <MenuGroupHeader>Appearance</MenuGroupHeader>
                  {THEME_OPTIONS.map(({ value, label, icon }) => (
                    <MenuItemRadio key={value} name="theme" value={value} icon={icon}>
                      {label}
                    </MenuItemRadio>
                  ))}
                  <MenuDivider />
                  <MenuItem icon={<SignOutRegular />} onClick={handleSignOut}>
                    Sign out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        )}
      </div>

      <div className={`${styles.drawerRoot} ${drawerOpen ? styles.drawerRootOpen : ''}`}>
        <div
          className={`${styles.drawerBackdrop} ${drawerOpen ? styles.drawerBackdropVisible : ''}`}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
        <aside
          className={`${styles.drawerSheet} ${drawerOpen ? styles.drawerSheetOpen : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <div className={styles.drawerHeader}>
            <img src={htsLogos.white} alt="Harris Technology Services" className={styles.drawerLogo} />
            <Button
              appearance="subtle"
              aria-label="Close navigation menu"
              icon={<DismissRegular />}
              className={styles.drawerClose}
              onClick={() => setDrawerOpen(false)}
            />
          </div>
          <div className={styles.drawerDivider} />
          <div className={styles.drawerSearchRow}>
            <SearchRegular className={styles.drawerSearchIcon} />
            <input
              type="text"
              placeholder="Search"
              aria-label="Search"
              className={styles.drawerSearchInput}
            />
          </div>
          <div className={styles.drawerBody}>
            <nav aria-label="Primary" className={styles.drawerNav}>
              {NAV_ITEMS.map((item) => {
                const isActive = isNavItemActive(location.pathname, item.path);
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    type="button"
                    aria-current={isActive ? 'page' : undefined}
                    className={`${styles.drawerItem} ${isActive ? styles.drawerItemActive : ''}`}
                    onClick={() => {
                      setDrawerOpen(false);
                      navigate(item.path);
                    }}
                  >
                    {isActive && <span className={styles.drawerIndicator} />}
                    <Icon className={styles.drawerIcon} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>
      </div>
    </header>
  );
};

export default Header;
